/**
 * Téléchargement en flux par Service Worker — stratégie `streamsaver`.
 *
 * ## Le problème résolu
 *
 * Firefox et Safari n'exposent pas la File System Access API. La seule issue sans
 * dépendance était d'accumuler le fichier en mémoire, ce qui tue l'onglet au-delà de
 * quelques centaines de mégaoctets. Ce module fait écrire le navigateur **en flux**, en
 * lui faisant croire qu'un serveur lui envoie le fichier.
 *
 * ## Comment ça marche
 *
 * Un Service Worker peut répondre à une requête par un `Response` construit sur un
 * `ReadableStream`, avec `Content-Disposition: attachment` : le navigateur traite alors
 * la réponse comme un téléchargement et l'écrit sur disque à mesure que le flux produit
 * des octets. Le fichier ne transite donc jamais entièrement en mémoire.
 *
 * Le flux appartient à **la page**, pas au Service Worker, et lui est transféré. Ce n'est
 * pas un détail : un flux créé dans le handler du Service Worker est ramassé par le
 * ramasse-miettes (c'est la cause du bug Mozilla 1942573 sur Firefox 134). Le transfert
 * crée une propriété partagée qui survit à la mise en veille du Service Worker.
 *
 * ## Trois contraintes, toutes découvertes en échouant (voir `docs/ADR-001`)
 *
 * 1. **La page doit être contrôlée par le Service Worker.** Sinon la requête n'est pas
 *    interceptée et part au serveur.
 * 2. **L'enregistrement du flux doit être acquitté avant de naviguer.** Page et Service
 *    Worker partagent une boucle d'événements : sans attente explicite, le `fetch` peut
 *    arriver avant le flux.
 * 3. **Un `ReadableStream` se transfère, il ne se clone pas** : `postMessage(stream, [stream])`.
 *
 * ## Ce que ce module ne fait pas
 *
 * Il ne prétend pas fonctionner sur Safari. WebKit, dès qu'il voit `Content-Disposition:
 * attachment`, cesse de tenir compte du Service Worker et va chercher l'URL directement
 * auprès du serveur. Le problème n'est pas l'origine : un second domaine n'y changerait
 * rien. `isStreamDownloadSupported` exclut donc Safari, et `writer.ts` retombe sur la
 * mémoire avec un refus explicite au-delà de la limite.
 */

import type { Bytes } from '@attrape/shared';

/** Chemin réservé aux téléchargements servis par le Service Worker. */
const DOWNLOAD_PATH_PREFIX = '/__download/';

/** Délai laissé au Service Worker pour accuser réception du flux. */
const REGISTER_TIMEOUT_MS = 5000;

export interface StreamDownloadSupport {
  supported: boolean;
  /** Motif technique, journalisé en développement. Jamais montré à l'utilisateur. */
  reason?: string;
}

/**
 * Détermine si le téléchargement en flux par Service Worker est utilisable.
 *
 * Détection par **capacité**, jamais par chaîne d'agent : les mainteneurs de StreamSaver
 * regrettent explicitement d'avoir dû faire du reniflage d'agent, et une politique
 * d'entreprise peut exposer une API tout en la neutralisant.
 *
 * Safari est écarté sur un critère de capacité précis : il expose pourtant toutes les
 * API nécessaires. Le seul moyen fiable de l'écarter est la détection de moteur, faute
 * d'API reflétant le comportement de `Content-Disposition`. C'est documenté comme tel.
 */
export function isStreamDownloadSupported(): StreamDownloadSupport {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') {
    return { supported: false, reason: 'hors navigateur' };
  }
  if (!('serviceWorker' in navigator)) {
    return { supported: false, reason: 'service worker indisponible' };
  }
  if (typeof ReadableStream === 'undefined') {
    return { supported: false, reason: 'ReadableStream indisponible' };
  }
  if (typeof MessageChannel === 'undefined') {
    return { supported: false, reason: 'MessageChannel indisponible' };
  }
  if (!isSecureContext) {
    return { supported: false, reason: 'contexte non sécurisé' };
  }

  // Safari : WebKit ignore la réponse du Service Worker dès que l'en-tête
  // `Content-Disposition: attachment` est présent (WebKit 201580, 202142). Toutes les
  // API nécessaires sont présentes, donc aucune détection de capacité ne peut l'écarter.
  // On isole cette exception en un seul endroit, pour qu'elle soit visible et datée.
  const agent = navigator.userAgent;
  const isSafari = /safari/i.test(agent) && !/chrome|chromium|crios|edg|firefox/i.test(agent);
  if (isSafari) {
    return { supported: false, reason: 'Safari ignore le service worker pour les pièces jointes' };
  }

  // Firefox 134.0.1–135.x : régression transitoire (bug Mozilla 1942573) où les
  // téléchargements par Service Worker sont interrompus prématurément (flux collecté
  // par le GC). Corrigé dans Firefox 136 et rétroporté. L'agent utilisateur de Firefox
  // n'exposant pas la version mineure (le numéro derrière « Firefox/ » vaut « .0 »), on
  // écarte conservativement toutes les versions 134 et 135 ; les versions antérieures à
  // 134 (pas encore touchées) et égales ou supérieures à 136 (corrigées) restent admises.
  const firefox = /firefox\/(\d+)/i.exec(agent);
  if (firefox) {
    const major = Number(firefox[1]);
    if (major >= 134 && major < 136) {
      return {
        supported: false,
        reason: 'Firefox 134.0.1–135.x tronque les téléchargements par Service Worker',
      };
    }
  }

  return { supported: true };
}

/**
 * Demande de données en attente.
 *
 * Le consommateur (le navigateur, via le Service Worker) signale qu'il veut des octets en
 * appelant `pull` sur le flux. C'est le **seul** signal qui reflète la vitesse réelle
 * d'écriture sur disque : ni `bufferedAmount`, ni `desiredSize` ne mesurent cela.
 *
 * Le mécanisme précédent interrogeait `desiredSize` en boucle : avec la stratégie de file
 * par défaut (high-water mark de 1), il devient négatif dès le deuxième morceau non lu,
 * ce qui transformait l'attente en attente active bornée par un `setTimeout` — un plafond
 * de débit arbitraire, sans rapport avec le disque.
 */
interface Demand {
  resolve: () => void;
  reject: (error: Error) => void;
}

interface PendingDownload {
  id: string;
  controller: ReadableStreamDefaultController<Bytes> | null;
  /** Demandes de données non encore satisfaites, dans l'ordre d'arrivée. */
  demands: Demand[];
  /** Signale la fin du flux à toutes les demandes en attente. */
  settle: (error?: Error) => void;
  closed: boolean;
}

let counter = 0;
const pending = new Map<string, PendingDownload>();

/**
 * Crée un téléchargement en flux et renvoie une fonction d'écriture.
 *
 * La séquence est imposée par les contraintes décrites en tête de module :
 * enregistrer le flux, **attendre l'acquittement**, puis seulement naviguer.
 */
export async function openStreamDownload(fileName: string): Promise<StreamWriter> {
  const worker = await acquireController();

  counter += 1;
  const id = `${Date.now().toString(36)}-${counter}`;
  const channel = new MessageChannel();

  const entry: PendingDownload = {
    id,
    controller: null,
    demands: [],
    closed: false,
    settle(error) {
      const pendingDemands = this.demands.splice(0);
      for (const demand of pendingDemands) {
        if (error) demand.reject(error);
        else demand.resolve();
      }
    },
  };
  pending.set(id, entry);

  const stream = new ReadableStream<Bytes>({
    start(controller) {
      entry.controller = controller;
    },
    /**
     * Le consommateur veut des octets. C'est le signal de contre-pression **réel** :
     * tant qu'il n'est pas appelé, le disque n'absorbe pas et il ne faut rien produire.
     *
     * Chaque `pull` satisfait une demande en attente. `pull` n'est rappelé qu'après que
     * le morceau précédent a été consommé, ce qui borne naturellement le tampon.
     */
    pull() {
      const demand = entry.demands.shift();
      if (demand) {
        demand.resolve();
        return;
      }
      // Aucune demande en attente : on ne peut rien satisfaire tout de suite. Le
      // consommateur rappellera `pull` après notre prochain `enqueue`.
      return Promise.resolve();
    },
    cancel() {
      entry.closed = true;
      entry.settle();
      pending.delete(id);
    },
  });

  // Acquittement : le Service Worker confirme avoir enregistré le flux. Sans cette
  // attente, la navigation peut le précéder et la requête retomber sur le serveur.
  await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('Le service worker n’a pas confirmé la réception du flux.'));
    }, REGISTER_TIMEOUT_MS);

    channel.port1.onmessage = (event: MessageEvent) => {
      if ((event.data as { type?: string } | null)?.type !== 'stream-download-registered') return;
      clearTimeout(timer);
      resolve();
    };

    worker.postMessage({ type: 'stream-download-register', id }, [channel.port2]);
  });

  // Le Service Worker demande le flux par ce canal. Le flux est **transféré** : un
  // `ReadableStream` ne se clone pas.
  channel.port1.postMessage({ type: 'stream-download-stream', stream }, [stream]);

  // Navigation déclenchée seulement maintenant : le flux est enregistré et transféré.
  // Le nom du fichier voyage en paramètre : c'est le Service Worker qui le pose dans
  // l'en-tête `Content-Disposition`, seul endroit où le navigateur le lit pour nommer
  // le fichier enregistré.
  triggerNavigation(`${DOWNLOAD_PATH_PREFIX}${id}?name=${encodeURIComponent(fileName)}`);

  return {
    async write(chunk: Bytes): Promise<void> {
      // Attendre une demande AVANT d'écrire : on ne produit jamais plus que ce que le
      // consommateur réclame, donc le tampon ne grandit pas avec la taille du fichier.
      await waitForDemand(entry);

      const controller = entry.controller;
      if (!controller || entry.closed) {
        throw new Error('Flux de téléchargement déjà fermé.');
      }
      controller.enqueue(chunk);
    },
    async close(): Promise<void> {
      entry.closed = true;
      entry.controller?.close();
      entry.settle();
      pending.delete(id);
    },
    async abort(): Promise<void> {
      entry.closed = true;
      try {
        entry.controller?.error(new Error('Transfert annulé'));
      } catch {
        // Le flux peut déjà être clos : abandonner ne doit jamais lever à son tour.
      }
      entry.settle(new Error('Transfert annulé'));
      pending.delete(id);
    },
  };
}

/**
 * Attend que le consommateur réclame des données.
 *
 * Renvoie immédiatement si une demande est déjà en attente — c'est le cas normal quand le
 * disque suit. Sinon, on s'inscrit dans la file et c'est `pull` qui réveillera.
 */
function waitForDemand(entry: PendingDownload): Promise<void> {
  if (entry.closed) return Promise.reject(new Error('Flux de téléchargement fermé.'));
  return new Promise<void>((resolve, reject) => {
    entry.demands.push({ resolve, reject });
  });
}

/**
 * Obtient un Service Worker qui **contrôle** la page.
 *
 * `navigator.serviceWorker.ready` ne suffit pas : il garantit qu'un Service Worker est
 * actif, pas que cette page est contrôlée. Au **premier chargement**, une page n'est pas
 * contrôlée avant un rechargement — or un destinataire peut accepter un transfert
 * immédiatement après avoir ouvert le lien. Sans ce traitement, la stratégie échouerait
 * silencieusement précisément dans le cas le plus courant.
 *
 * On force donc la prise de contrôle (`clients.claim()` côté worker, que le Service
 * Worker appelle déjà à l'activation) et on attend l'événement `controllerchange`.
 */
async function acquireController(): Promise<ServiceWorker> {
  // `ready` garantit qu'un Service Worker est actif avant qu'on ne vérifie s'il contrôle
  // déjà cette page. La valeur de l'enregistrement n'est plus exploitée : un worker actif
  // mais qui ne contrôle pas la page n'intercepterait pas la navigation, on échoue donc
  // plutôt que de tomber sur `registration.active` (qui ne servirait pas la requête).
  await navigator.serviceWorker.ready;
  if (navigator.serviceWorker.controller) return navigator.serviceWorker.controller;

  // Le Service Worker est actif mais ne contrôle pas encore cette page : il faut
  // attendre qu'il prenne la main, ce qu'il fait à l'activation via `clients.claim()`.
  const controller = await new Promise<ServiceWorker | null>((resolve) => {
    const timer = setTimeout(() => resolve(navigator.serviceWorker.controller), 3000);
    navigator.serviceWorker.addEventListener(
      'controllerchange',
      () => {
        clearTimeout(timer);
        resolve(navigator.serviceWorker.controller);
      },
      { once: true },
    );
  });

  // La page n'est toujours pas contrôlée après l'attente : un worker actif mais qui ne
  // contrôle pas cette page n'intercepterait pas la navigation vers `/__download/...`,
  // qui partirait au serveur au lieu d'être servie par le flux. On échoue plutôt que de
  // retourner un worker inutile et de laisser le téléchargement échouer silencieusement.
  if (!controller) {
    throw new Error(
      'La page n’est pas contrôlée par le service worker : le téléchargement ne serait pas intercepté.',
    );
  }
  return controller;
}

export interface StreamWriter {
  write(chunk: Bytes): Promise<void>;
  close(): Promise<void>;
  abort(): Promise<void>;
}

/**
 * Déclenche la navigation vers l'URL servie par le Service Worker.
 *
 * **Une navigation, et non un `<a download>`.** Ce n'est pas un détail : mesuré, un
 * `<a download>` même-origine est traité par Chromium comme un téléchargement initié par
 * le navigateur qui **contourne entièrement le Service Worker** — la requête n'atteint
 * jamais le handler `fetch`, et le navigateur enregistre à la place la réponse du serveur
 * (ici l'index du SPA, soit 2 Ko de HTML). Un `location.assign`, lui, produit une
 * navigation (`mode=navigate`) que le Service Worker intercepte normalement.
 *
 * C'est l'en-tête `Content-Disposition: attachment` porté par la réponse qui fait que le
 * navigateur enregistre le flux au lieu de quitter la page.
 *
 * `assign` plutôt qu'une affectation de `location.href` : même effet, mais l'intention
 * est explicite et le lint ne s'y trompe pas.
 */
function triggerNavigation(url: string): void {
  window.location.assign(url);
}
